import { REFERRAL_CODE_LOCALSTORAGE_KEY } from '@/shared/constants/localstorage';
import { logWarn } from '@/shared/lib/logging';

/**
 * Client-side referral attribution ("cookie window").
 *
 * When a visitor lands on any page with `?ref=CODE`, the code is persisted in
 * localStorage so it survives navigation until the actual sign-up, where it is
 * sent to the backend. The stored code expires after REFERRAL_TTL_DAYS: this
 * mirrors the program's cookie window (the value configured server-side in
 * ReferralProgram.cookie_window_days).
 */

const REFERRAL_TTL_DAYS = 60;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface StoredReferral {
    code: string;
    ts: number;
}

export function captureReferralCodeFromUrl(): void {
    try {
        const code = new URLSearchParams(window.location.search).get('ref');

        if (!code || !code.trim()) {
            return;
        }

        const payload: StoredReferral = {
            code: code.trim().toUpperCase(),
            ts: Date.now(),
        };

        localStorage.setItem(
            REFERRAL_CODE_LOCALSTORAGE_KEY,
            JSON.stringify(payload),
        );
    } catch (error) {
        logWarn('Failed to capture referral code:', error);
    }
}

export function getStoredReferralCode(): string | undefined {
    try {
        const raw = localStorage.getItem(REFERRAL_CODE_LOCALSTORAGE_KEY);

        if (!raw) {
            return undefined;
        }

        const stored = JSON.parse(raw) as StoredReferral;

        if (!stored.code || typeof stored.ts !== 'number') {
            return undefined;
        }

        if (Date.now() - stored.ts > REFERRAL_TTL_DAYS * MS_PER_DAY) {
            localStorage.removeItem(REFERRAL_CODE_LOCALSTORAGE_KEY);

            return undefined;
        }

        return stored.code;
    } catch (error) {
        logWarn('Failed to read referral code:', error);

        return undefined;
    }
}

export function clearStoredReferralCode(): void {
    try {
        localStorage.removeItem(REFERRAL_CODE_LOCALSTORAGE_KEY);
    } catch (error) {
        logWarn('Failed to clear referral code:', error);
    }
}
